/**
 * The engine's verdict on how a result set is shaped, passed through the API verbatim.
 *
 * - `compound` — exact lexical matches, then a relevance-ordered semantic tail. The only shape
 *   with a boundary, and the only one where rows carry a per-row exact marker.
 * - `fuzzy-complex` — nothing matched the words (too complex, or a typo), so the page is entirely
 *   semantic.
 * - `fuzzy-empty` — the words do match listings, but the hard filters admitted none of them: also
 *   entirely semantic, though the buyer fixes it by loosening a filter rather than rewording.
 * - `fuzzy-common` — a common term answered by a keyword+semantic blend throughout. The engine
 *   marks no rows here and there is no boundary to find, so the page stays one flat list.
 */
export type SearchComposition = 'compound' | 'fuzzy-complex' | 'fuzzy-common' | 'fuzzy-empty';

/**
 * The per-page fields the search endpoint adds, plus how many items that page held.
 *
 * Only the length is kept, never the items themselves — this gets cached across back-navigation,
 * and nothing here reads the products.
 */
export interface SearchSectionPage {
  listLength: number;
  composition?: SearchComposition;
  suggestionsStartAt?: number;
}

export interface SearchSections {
  /**
   * Index in the flattened product list where related items begin, or undefined when the boundary
   * is not on the pages fetched so far — either because every fetched row is still a match, or
   * because this result set has no boundary at all.
   */
  suggestionsStartAt?: number;
  /** True when every item shown is a related item rather than a match for the buyer's words. */
  suggestionsOnly: boolean;
  /**
   * Whether the engine marked rows at all, i.e. whether a result_type can honestly be stated for
   * an item.
   *
   * Distinct from `suggestionsStartAt` being undefined, and the distinction is load-bearing: an
   * all-matches-so-far page has no boundary yet but every row is a known match, whereas a
   * `fuzzy-common` page has no boundary because the engine never marked anything. Collapsing the
   * two makes impressions and clicks disagree about the same response.
   */
  marksResultType: boolean;
}

/** Compositions where every row is a related item, categorically — no per-row marker needed. */
const ALL_SEMANTIC: SearchComposition[] = ['fuzzy-complex', 'fuzzy-empty'];

/**
 * Folds the per-page section metadata of every fetched page into one boundary over the flattened
 * list.
 *
 * The engine orders matches ahead of related items across the whole ranked list, so each page's
 * matches are a prefix of that page and the global boundary is just their sum. A page deep in the
 * related-items tail reports 0 and adds nothing, which is why this sums rather than reading only
 * the first page.
 *
 * A page missing `suggestionsStartAt` contributes no matches. The API sets it in lockstep with
 * the `compound` composition, so that is defensive — and it errs the safe way: an unknown row is
 * shown as a suggestion, never presented to the buyer as something that matched their words.
 */
export function resolveSearchSections(pages: SearchSectionPage[]): SearchSections {
  const composition = pages[0]?.composition;

  if (composition !== undefined && ALL_SEMANTIC.includes(composition)) {
    return { suggestionsOnly: true, marksResultType: true };
  }

  // `fuzzy-common`, or an arm that says nothing: one flat list, and nothing to say per row.
  if (composition !== 'compound') {
    return { suggestionsOnly: false, marksResultType: false };
  }

  const matches = pages.reduce((sum, page) => sum + (page.suggestionsStartAt ?? 0), 0);
  const total = pages.reduce((sum, page) => sum + page.listLength, 0);

  // Every match was dropped between retrieval and display (sold, unlisted), so what is left is
  // entirely suggestions.
  if (matches === 0) {
    return { suggestionsOnly: true, marksResultType: true };
  }

  // The matches have not run out yet: no boundary to draw on what has been fetched, but every row
  // so far is still a known match.
  if (matches >= total) {
    return { suggestionsOnly: false, marksResultType: true };
  }

  return { suggestionsStartAt: matches, suggestionsOnly: false, marksResultType: true };
}

/** Whether one result matched the buyer's typed words, or is a related item shown alongside. */
export type SearchItemSection = 'exact' | 'suggested';

/**
 * Marks one result exact vs suggested, by its index in the flattened list.
 *
 * The single source of this marker for every analytics event on both platforms. Impressions and
 * clicks must agree about the same item, so they have to be answered by the same function reading
 * the same accumulated state — deriving one of them separately is what let them drift apart.
 *
 * Returns undefined when the engine marked nothing (`fuzzy-common`, or an arm reporting nothing),
 * and callers then omit the marker entirely rather than guessing. That matters most on
 * `fuzzy-common`: claiming those rows matched lexically would invent that fact about
 * semantically retrieved items, on the bulk of search traffic, inside the dataset the marker
 * exists to measure.
 */
export function resultTypeAt(sections: SearchSections, index: number): SearchItemSection | undefined {
  if (!sections.marksResultType) {
    return undefined;
  }

  if (sections.suggestionsOnly) {
    return 'suggested';
  }

  // No boundary on the fetched pages, and the engine did mark rows, so everything so far matched.
  if (sections.suggestionsStartAt === undefined) {
    return 'exact';
  }

  return index < sections.suggestionsStartAt ? 'exact' : 'suggested';
}

/** Event-level split of a page, for the one warehouse row that represents a `view_item_list`. */
export interface ResultTypeCounts {
  exact_count: number;
  suggested_count: number;
}

/**
 * Counts for a page's markers, or undefined when the engine marked nothing.
 *
 * Undefined rather than zeroes, and the difference matters: `exact_count: 0` asserts the page had
 * no exact matches, when the honest answer on `fuzzy-common` and on arms that report nothing is
 * that no claim was made. A warehouse column reads 0 and NULL differently, and emitting zeroes
 * would also add params to other arms' events mid-experiment.
 *
 * Shared rather than implemented per platform: web and mobile have twice drifted apart on exactly
 * this decision, once in each direction.
 */
export function resultTypeCounts(markers: (SearchItemSection | undefined)[]): ResultTypeCounts | undefined {
  if (!markers.some((marker) => marker !== undefined)) {
    return undefined;
  }

  return {
    exact_count: markers.filter((marker) => marker === 'exact').length,
    suggested_count: markers.filter((marker) => marker === 'suggested').length,
  };
}
